import { Card, CardContent, Button, Textarea, Stack } from "@altimateai/lego";
import { useState, useEffect } from "react";
import classNames from "classnames";
import { useBoolean } from "../../../helpers/hooks";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { GET_TABLES } from "../../../services/filters";
import { ModifiedByTag } from "../../../components/tags";
import { default as EditIcon } from "@/assets/icons/edit.svg?react";
import {
  updateTablePurpose,
  generateDescription,
  getTableDescriptionById,
} from "../../../helpers/apis";
import { default as RobotLogo } from "@/assets/icons/robot1.svg?react";
import ProcessingScreen from "@/assets/icons/processing_screen.gif";
import { GET_TABLE_BY_ID } from "../DatasetsDetails";
import styles from "../styles.module.scss";
import { UserListResponse } from "@/types";
import { UpdateTablePurposeRequest } from "../types";
import { DatasetsTracking } from "@/lib/tracking/datasets";

const GET_SINGLE_TABLE_DESCRIPTION = "GET-TABLE-DESCRIPTION";

interface PurposeSectionProps {
  tableId: string;
  purpose: string | null | undefined;
  users?: UserListResponse["users"];
  lastModifiedBy: number | null | undefined;
}

export const PurposeSection = ({
  tableId,
  purpose: defaultPurpose,
  users,
  lastModifiedBy,
}: PurposeSectionProps) => {
  const [purpose, setPurpose] = useState<string | null | undefined>(defaultPurpose);
  const queryClient = useQueryClient();
  const {
    value: isPurposeEditing,
    setTrue: setPurposeEditing,
    setFalse: unsetPurposeEditing,
  } = useBoolean();
  const { mutate } = useMutation({
    mutationFn: (data: UpdateTablePurposeRequest) => updateTablePurpose(tableId, data),
    onSuccess: data => {
      if (data?.ok) {
        unsetPurposeEditing();
        queryClient.invalidateQueries({ queryKey: [GET_TABLES] });
        DatasetsTracking.purposeEdited(tableId);
      }
    },
  });

  const setDescription = useMutation({
    mutationFn: (data: Record<string, string>) => {
      return generateDescription(tableId, data);
    },
    onSuccess: data => {
      if (data?.ok) {
        queryClient.invalidateQueries({ queryKey: [GET_TABLE_BY_ID] });
        queryClient.invalidateQueries({ queryKey: [GET_SINGLE_TABLE_DESCRIPTION] });
        DatasetsTracking.aiDescriptionGenerated(tableId);
      }
    },
  });

  const { data } = useQuery({
    queryKey: [GET_SINGLE_TABLE_DESCRIPTION, tableId],
    queryFn: () => getTableDescriptionById(tableId),
    enabled: setDescription.isSuccess,
  });

  useEffect(() => {
    if (!setDescription.isSuccess) {
      return;
    }
    setPurpose(data?.description);
  }, [setDescription.isSuccess, data?.description]);

  return (
    <>
      <Card className={classNames("mb-0", styles.bg_gray)}>
        <CardContent>
          <Stack direction="column" gap="sm" className="al-pt-4">
            <Stack gap="xs" align="center" justify="between">
              <div className="fs-5 fw-semibold">Generate Description for the table and columns</div>
              {purpose ? (
                <Button
                  className={styles.generate_button_disabled}
                  size="sm"
                  disabled
                  aria-label="Generate description"
                >
                  <Stack gap="xs" align="center">
                    <RobotLogo />
                    Generate
                  </Stack>
                </Button>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setDescription.mutateAsync({});
                  }}
                  aria-label="Generate description"
                >
                  <Stack gap="xs" align="center">
                    <RobotLogo />
                    {setDescription.isPending ? "Generating..." : "Generate"}
                  </Stack>
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {purpose && (
        <Card className={styles.table_description} aria-label="Table description section">
          <CardContent className="al-pt-4">
            <Stack gap="xs" align="center" className="mb-4">
              <div>Description</div>
              <button
                type="button"
                className="al-bg-transparent al-border-none al-p-0 al-cursor-pointer"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (isPurposeEditing) {
                    setPurpose(defaultPurpose);
                    unsetPurposeEditing();
                  } else {
                    setPurposeEditing();
                  }
                }}
                aria-label="Edit table description"
              >
                <EditIcon
                  className={classNames("cursor-pointer", styles.edit_icon, {
                    [styles.active]: isPurposeEditing,
                  })}
                />
              </button>
              <div className="spacer" />
              <ModifiedByTag users={users} lastModifiedBy={lastModifiedBy} />
            </Stack>
            <div>
              {isPurposeEditing ? (
                <>
                  <Textarea
                    disabled={!isPurposeEditing}
                    rows={defaultPurpose ? 4 : 2}
                    className={styles.multiline_input}
                    value={purpose || ""}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setPurpose(e.target.value)
                    }
                    placeholder="Enter the purpose for the table"
                  />
                  <Stack align="center" gap="xs" className="mt-2">
                    <Button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        mutate({ purpose });
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      className={styles.cancel_button}
                      onClick={() => {
                        setPurpose(defaultPurpose);
                        unsetPurposeEditing();
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </>
              ) : (
                <div className="text-muted font-normal">{purpose}</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {setDescription.isPending && (
        <Card className={styles.table_description}>
          <CardContent className="al-pt-4">
            <Stack
              gap="xs"
              align="center"
              direction="column"
              className={classNames(styles.processing_div)}
            >
              <img src={ProcessingScreen} className={classNames(styles.gif_img)} alt="Processing" />
              <div className="spacer" />
              <div className="text-muted font-normal">Checking query history</div>
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
};
